import "./TaskPredecessors.css";
import * as React from 'react';
import { Icon, IconButton, PrimaryButton } from 'office-ui-fabric-react';
import AbbreviationIcon from '../AbbreviationIcon';
import { IPredecessorInfo, ITask, PredecessorType, PredecessorTypeMetadata } from '../../../entities/Subentities';
import { FormatType } from "../../../entities/Metadata";
import { FormatDate, formatValue } from "../../utils/common";
import { MaybeDate } from '../../../entities/common';
import Link from '../../common/Link';

interface Props {
    readonly: boolean;
    onEdit: (predecessor: IPredecessorInfo) => void;
    onRemove: (predecessor: IPredecessorInfo) => void;
    predecessor: IPredecessorInfo;
    task?: ITask;
    allowEdit: boolean;
    isExternal?: boolean;
    onAccept?: (predecessorId: string) => void;
}

const TaskPredecessor = (props: Props) => {
    const { readonly, predecessor, task, onRemove, allowEdit, onEdit, isExternal, onAccept } = props;
    const displayName = React.useMemo(() => isExternal && predecessor.projectName
        ? `${predecessor.name} (${predecessor.projectName})`
        : predecessor.name,
        [isExternal, predecessor.name, predecessor.projectName]);
    const taskUrl = React.useMemo(() => isExternal ? `/project/task/${predecessor.id}` : undefined, [isExternal, predecessor.id]);
    const lagLabel = React.useMemo(() => `${formatValue(predecessor.lag, FormatType.Days)}`, [predecessor.lag]);
    const lagTitle = React.useMemo(() => `${Math.abs(predecessor.lag)} ${Math.abs(predecessor.lag) !== 1 ? "days" : "day"} ${predecessor.lag < 0 ? "lead" : "lag"} time`,
        [predecessor.lag]);

    const isFromStart = predecessor.type === PredecessorType.StartToStart || predecessor.type === PredecessorType.StartToFinish;
    const startDateSnapshot = isExternal && task && (isFromStart || predecessor.startDate)
        && !areDatesEqual(predecessor.startDate, task.attributes.StartDate)
        ? predecessor.startDate : undefined;
    const dueDateSnapshot = isExternal && task && (!isFromStart || predecessor.dueDate)
        && !areDatesEqual(predecessor.dueDate, task.attributes.DueDate)
        ? predecessor.dueDate : undefined;
    const isDateChanged = startDateSnapshot !== undefined || dueDateSnapshot !== undefined;
    const isDurationChanged = isExternal && predecessor.type === PredecessorType.FinishToStart && task && (predecessor.duration === 0) !== (task.attributes.Duration === 0);
    const canAccept = !!onAccept && allowEdit && isExternal && (isDateChanged || isDurationChanged);

    return <div className="predecessor-box align-center">
        <AbbreviationIcon abbreviation={PredecessorTypeMetadata[predecessor.type].abbreviation}
            className={isExternal ? "external" : undefined}
            title={`${PredecessorTypeMetadata[predecessor.type].label} dependency type`} />
        <div className="predecessor-content">
            <div className="predecessor-name ellipsis" title={displayName}>
                {taskUrl
                    ? <Link href={taskUrl} target="_blank" title={`Project: ${predecessor.projectName}`} className="predecessor-name-link">{displayName}</Link>
                    : displayName}
            </div>
            <div className="predecessor-meta">
                <div className="predecessor-meta-row" title={lagTitle}>
                    <Icon iconName={"PublicCalendar"} />
                    <span>Lag: {lagLabel}</span>
                </div>
                {isExternal && <>
                    {startDateSnapshot !== undefined && <DateLabel title="Start Date" value={task?.attributes.StartDate} snapshotValue={startDateSnapshot} />}
                    {dueDateSnapshot !== undefined && <DateLabel title="Due Date" value={task?.attributes.DueDate} snapshotValue={dueDateSnapshot} />}
                    {isDurationChanged && <DurationLabel title="Duration" value={task.attributes.Duration} snapshotValue={predecessor.duration} />}
                </>}
            </div>
        </div>
        <div className={`predecessor-actions ${canAccept ? 'with-accept' : ''}`}>
            {canAccept && !readonly && (
                <PrimaryButton className="predecessor-accept-btn" iconProps={{ iconName: 'Completed' }} onClick={() => onAccept?.(predecessor.id)}>
                    Accept
                </PrimaryButton>
            )}
            <div className="predecessor-icon-actions">
                {!readonly && <IconButton disabled={!allowEdit} menuIconProps={{ iconName: 'Edit', style: { fontSize: '16px' } }} title="Edit" onClick={() => onEdit(predecessor)} />}
                {!readonly && <IconButton menuIconProps={{ iconName: 'Delete', style: { fontSize: '16px' } }} title="Delete" onClick={() => onRemove(predecessor)} />}
            </div>
        </div>
    </div>;
}

function DateLabel(props: Readonly<{ title: string; value?: MaybeDate; snapshotValue?: MaybeDate }>) {
    return <div className="predecessor-meta-row">
        <Icon iconName="Calendar" />
        <span>{props.title}: {props.value ? FormatDate(props.value) : '-'}</span>
        {props.snapshotValue !== undefined && <span className="predecessor-source-date"><Icon iconName="Warning" />Previous value: {FormatDate(props.snapshotValue) ?? '-'}</span>}
    </div>;
}

function DurationLabel(props: Readonly<{ title: string; value?: number | null; snapshotValue?: number | null }>) {
    return <div className="predecessor-meta-row">
        <Icon iconName="Clock" />
        <span>{props.title}: {props.value !== undefined && props.value !== null ? formatValue(props.value, FormatType.Days) : '-'}</span>
        {props.snapshotValue !== undefined && <span className="predecessor-source-date"><Icon iconName="Warning" />Previous value: {formatValue(props.snapshotValue, FormatType.Days) ?? '-'}</span>}
    </div>;
}

function areDatesEqual(left?: MaybeDate, right?: MaybeDate): boolean {
    if (!left && !right) {
        return true;
    }

    if (!left || !right) {
        return false;
    }

    return new Date(left as string).toISOString().slice(0, 10) === new Date(right as string).toISOString().slice(0, 10);
}

export default TaskPredecessor;
